/* FAQ page: one question open at a time.

   The rows are native <details>, which have no exclusivity of their own, so
   opening one has to close the rest. It is page-wide, not per section -- open
   a question under SHIPPING and an open one under PRODUCT closes too.

   The listener is on <main> in the CAPTURE phase on purpose: `toggle` does
   not bubble, so a listener on an ancestor only ever sees it on the way
   down. Guarded with a data flag so a theme-editor section reload cannot
   stack a second listener on the same element.

   Horizon's accordion-custom.js also writes details.open, but only when the
   mobile breakpoint is crossed, so the two never fight in normal use. */
(function () {
  var SCOPE = '.content-for-layout[data-template="page.faq"]';

  function wire() {
    var main = document.querySelector(SCOPE);
    if (!main || main.dataset.irrFaqSolo) return;
    main.dataset.irrFaqSolo = '1';

    main.addEventListener(
      'toggle',
      function (event) {
        var opened = event.target;
        if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;

        main.querySelectorAll('details[open]').forEach(function (other) {
          if (other !== opened) other.open = false;
        });
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
  document.addEventListener('shopify:section:load', wire);
})();

/* Cookie banner vs the product page's floating add-to-cart.

   Shopify's consent banner is fixed to the bottom at z-index 2000000, which
   is well above anything the theme can set, so it sat on top of the buy
   button -- the one control the page exists for. Rather than fight the
   stacking order, measure how much of the bottom of the window the banner
   is occupying and publish it as --irr-cookie-h; irr-pages.css feeds that
   into the bar's bottom offset through max(). Clearing the property puts
   the bar back where it was, so dismissal needs no undo step.

   Shopify injects the banner after load and removes it on a choice, and it
   reflows on rotate, hence the observers. Measuring from the banner's own
   top rather than its height covers whatever inset it is sitting on.

   This observer stays on the whole body: the banner is injected by Shopify
   and the depth it lands at is not ours to assume. The callback is one
   getElementById behind a rAF, so it is cheap -- unlike the anchor scan
   below, which is the one that had to be fixed. */
(function () {
  var root = document.documentElement;
  var GAP = 10;
  var queued = false;
  var watched = null;
  var sizer = null;

  function visible(el) {
    var css = getComputedStyle(el);
    return css.display !== 'none' && css.visibility !== 'hidden' && css.opacity !== '0';
  }

  function measure() {
    queued = false;
    var banner = document.getElementById('shopify-pc__banner');
    var box = banner && banner.getBoundingClientRect();

    if (!banner || !box.height || !visible(banner)) {
      root.style.removeProperty('--irr-cookie-h');
    } else {
      var lift = Math.round(window.innerHeight - box.top + GAP);
      root.style.setProperty('--irr-cookie-h', (lift > 0 ? lift : 0) + 'px');
    }

    if (banner !== watched) {
      watched = banner;
      if (sizer) sizer.disconnect();
      if (banner && window.ResizeObserver) {
        sizer = new ResizeObserver(schedule);
        sizer.observe(banner);
      }
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  }

  schedule();
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule);
})();

/* Collection page: fit the cards and the email signup into one screen.

   Everything other than the product photo is fixed-height chrome, but its
   total is not a constant -- the signup section's padding scales with the
   viewport, so it runs ~431px on a 900px screen and ~468px on a 1080px
   one. Measure the parts and let CSS size the grid from what is left.

   Every part measured here is independent of the photo's size, so
   resizing the grid cannot change the answer and there is no feedback
   loop. irr-pages.css carries a constant as the fallback. */
(function () {
  var SCOPE = '.content-for-layout[data-template="collection"]';
  var root = document.documentElement;
  var queued = false;

  function h(el) { return el ? Math.ceil(el.getBoundingClientRect().height) : 0; }
  function padY(el) {
    if (!el) return 0;
    var c = getComputedStyle(el);
    return parseFloat(c.paddingTop) + parseFloat(c.paddingBottom);
  }

  function measure() {
    queued = false;
    var main = document.querySelector(SCOPE);
    var grid = main && main.querySelector('.product-grid');
    if (!grid || window.innerWidth < 990) {
      root.style.removeProperty('--irr-col-grid-max');
      return;
    }

    var card = main.querySelector('.product-card__content');
    var chrome =
      h(document.querySelector('.header-section')) +
      padY(main.querySelector('.product-grid-container')) +
      padY(card) +
      h(card && card.lastElementChild) +
      h(document.querySelector('.irr-su'));

    var media = Math.max(160, window.innerHeight - chrome);
    root.style.setProperty('--irr-col-grid-max', (media * 2) + 'px');
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  }

  schedule();
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule);
  document.addEventListener('shopify:section:load', schedule);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  }
  window.addEventListener('load', schedule);
})();

/* One buy link for the 150mL serum, everywhere.

   The two sizes are two separate products and inventory is managed on the
   variant, so every route into the big serum should carry its variant id
   rather than relying on the bare product URL resolving to it. Same link
   the PDP's size toggle uses.

   Done here rather than at each source because the sources are scattered
   and not all of them are ours: SHOP lives in a Shopify navigation menu,
   which is store-level data shared with the live theme, and Continue
   shopping is Horizon's own cart markup. Rewriting the href on the way out
   touches neither. Links still work untouched if this never runs -- the
   product has a single variant, so the bare URL already lands on it; this
   only makes it explicit.

   PERFORMANCE, and why this is written the way it is. It used to run
   querySelectorAll('a[href]') across the WHOLE DOCUMENT and loop every
   anchor on the page, on a 50ms timer, every time anything in the body
   mutated -- plus once more on every pointerdown. Apps and the Klaviyo
   popup mutate the body constantly, so that full scan was running over and
   over while the page was simply being used, which is a real part of why
   the site felt slow and why closing the popup felt like a freeze.

   It now does work proportional to what actually changed: the observer
   reads the added nodes out of the records and scans only inside those. The
   opening pass is the only one that ever touches the whole document, and
   the pointerdown listener is gone. Do not reintroduce a document-wide
   scan here. */
(function () {
  var BUY_150 = '/products/face-and-body-serum?variant=50045716660504';
  var BARE = '/products/face-and-body-serum';

  function fix(a) {
    var href = a.getAttribute('href');
    if (!href) return;

    /* The product's own URL, with nothing already on it. Anything that
       arrives with a query string is left alone -- it is a search result
       or a campaign link and its parameters are not ours to drop. */
    if (href === BARE || href === BARE + '/') {
      a.setAttribute('href', BUY_150);
      return;
    }

    /* Empty-cart and not-found buttons, which Horizon points at a
       collection. There is one product line, so a collection is a detour. */
    if (a.classList.contains('cart-items__empty-button') ||
        a.textContent.trim().toLowerCase() === 'continue shopping') {
      a.setAttribute('href', BUY_150);
    }
  }

  function retarget(root) {
    var scope = root || document;
    if (scope.nodeType === 1 && scope.matches && scope.matches('a[href]')) fix(scope);
    if (!scope.querySelectorAll) return;
    var links = scope.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) fix(links[i]);
  }

  retarget();

  /* The menu drawer and the cart drawer hydrate after first paint, so the
     opening pass alone misses them. Only the nodes that arrived are read --
     attribute changes are not observed, so rewriting an href cannot
     retrigger this. */
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) retarget(added[j]);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', function (e) {
    retarget(e && e.target);
  });
  window.addEventListener('load', function () { retarget(); });
})();
